import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import SuspenseLoader from 'src/components/SuspenseLoader';
import {
  AdminWorkspaceProvider,
  AdminWorkspaceState,
  useAdminWorkspace
} from './AdminWorkspaceProvider';

/**
 * Legacy `/dashboards/*` routes that a Company Admin cannot use: their APIs are
 * platform-scoped and answer 403 `TENANT_CONTEXT_REQUIRED` for a company
 * session, so the page renders a loaded-looking shell with no data and no
 * explanation. None are reachable from the company navigation — they are
 * reached by URL, bookmark or back-button — so send the session back to its own
 * workspace instead of rendering an empty page (refs #1656, F10).
 *
 * Anything not listed here stays reachable; this is deliberately a deny-list so
 * a working legacy route is never blocked by omission.
 */
const COMPANY_UNSUPPORTED_LEGACY_ROUTES: RegExp[] = [
  /^\/dashboards\/home(\/|$)/,
  /^\/dashboards\/roles(\/|$)/,
  /^\/dashboards\/faqs(\/|$)/,
  /^\/dashboards\/(settings|contactus)(\/|$)/,
  /^\/dashboards\/activites(\/|$)/,
  /^\/dashboards\/documentReview(\/|$)/,
  /^\/dashboards\/newApplicants(\/|$)/
];

export const isCompanyUnsupportedLegacyRoute = (pathname: string): boolean =>
  COMPANY_UNSUPPORTED_LEGACY_ROUTES.some((pattern) => pattern.test(pathname));

export const resolveDashboardBoundary = (
  state: AdminWorkspaceState,
  pathname = ''
): 'ready' | 'loading' | '/login' | '/platform' | '/status/500' | string => {
  if (state.kind === 'legacy') return 'ready';
  if (state.kind === 'invalid-realm-session') return '/login';
  if (state.kind === 'platform') {
    // Company Profile links use these retained, authenticated event routes.
    if (/^\/dashboards\/jobTracker\/positions\/[1-9]\d*(?:\/bids)?\/?$/.test(pathname)) return 'ready';
    if (/^\/dashboards\/(gear-catalog|venues)\/?$/.test(pathname)) return 'ready';
    if (/^\/dashboards\/activites\/?$/.test(pathname)) return '/platform/scheduled-activity';
    if (/^\/dashboards\/roles\/?$/.test(pathname)) return '/platform/roles';
    if (/^\/dashboards\/faqs\/?$/.test(pathname)) return '/platform/faqs';
    if (/^\/dashboards\/(settings|contactus)\/?$/.test(pathname)) return '/platform/settings';
    return '/platform';
  }
  if (state.status === 'loading') return 'loading';
  if (state.status === 'error') return '/status/500';
  if (/^\/dashboards\/activites\/?$/.test(pathname)) return `${state.workspace.routeBase}/scheduled-activity`;
  if (/^\/dashboards\/venues\/?$/.test(pathname)) return `${state.workspace.routeBase}/venues`;
  if (/^\/dashboards\/operations\/?$/.test(pathname)) {
    return `${state.workspace.routeBase}/operations`;
  }
  // Adapt only exact retained surfaces after resolving the live company grant.
  if (/^\/dashboards\/documentReview\/?$/.test(pathname))
    return `${state.workspace.routeBase}/compliance`;
  if (/^\/dashboards\/newApplicants\/?$/.test(pathname))
    return `${state.workspace.routeBase}/applications`;
  if (isCompanyUnsupportedLegacyRoute(pathname)) {
    return `${state.workspace.routeBase}/dashboard`;
  }
  return 'ready';
};

const DashboardBoundaryContent = ({ children }: { children: ReactNode }) => {
  const state = useAdminWorkspace();
  const location = useLocation();
  const destination = resolveDashboardBoundary(state, location.pathname);

  if (destination === 'ready') return <>{children}</>;
  if (destination === 'loading') return <SuspenseLoader />;
  return <Navigate to={destination} replace />;
};

const LegacyDashboardBoundary = ({ children }: { children: ReactNode }) => (
  <AdminWorkspaceProvider>
    <DashboardBoundaryContent>{children}</DashboardBoundaryContent>
  </AdminWorkspaceProvider>
);

export default LegacyDashboardBoundary;
